import { useState } from "react";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useAuthController } from "@camberi/firecms";
import axios from "axios";

import ListItemText from "@mui/material/ListItemText";
import { useTheme } from "@mui/material/styles";
import LinearProgress from "@mui/material/LinearProgress";
import ListSubheader from "@mui/material/ListSubheader";
import DownloadIcon from "@mui/icons-material/Download";

const FIO_ALLOWED_DOWNLOADS = ["vid_h264_2160", "vid_h264_1080_best", "vid_h264_1080", "vid_h264_720", "vid_h264_540", "vid_h264_480", "vid_h264_360", "img_high", "img_full", "img_small", "img_cover", "src"];

const COCONUT_ALLOWED_GROUPS = ["video", "cover", "thumbnail", "hover", "other"];
const IMAGE_ALLOWED_GROUPS = ["source_proxy", "cover", "thumbnail", "image", "other"];

type FioPreviewContext = {
	provider: "fio";
	id: string;
	asset: any;
};

type CoconutPreviewContext = {
	provider: "coconut";
	id: string;
	job: any;
};

type ImagePreviewContext = {
	provider: "image";
	id: string;
	job: any;
};

type PreviewContext = FioPreviewContext | CoconutPreviewContext | ImagePreviewContext | null;

type DownloadItemData = {
	provider: "fio" | "coconut" | "image";
	asset_id: string;
	file_path: string;
	file_size: number;
	kind: "video" | "image" | "src" | "other";
	group?: string;
	role?: string;
	height?: number;
	rendition?: any;
};

type FrameDownloadFormat = "png" | "jpeg";

const isSignedUrlKey = (value: any) => {
	return String(value || "").includes("_signedUrl");
};

const isStoragePath = (value: any) => {
	return typeof value === "string" && value.startsWith("versions/") && !value.includes("_signedUrl");
};

const safeNumber = (value: any) => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const getCoconutJob = (versionValues: any, curPreviewAssetId: any): CoconutPreviewContext | null => {
	const coconutJobs = versionValues?.coconut_jobs || {};

	if (curPreviewAssetId && coconutJobs?.[curPreviewAssetId]) {
		return {
			provider: "coconut",
			id: String(curPreviewAssetId),
			job: coconutJobs[curPreviewAssetId],
		};
	}

	return null;
};

const getImageJob = (versionValues: any, curPreviewAssetId: any): ImagePreviewContext | null => {
	const imageJobs = versionValues?.image_jobs || {};

	if (curPreviewAssetId && imageJobs?.[curPreviewAssetId]) {
		return {
			provider: "image",
			id: String(curPreviewAssetId),
			job: imageJobs[curPreviewAssetId],
		};
	}

	return null;
};

const getFioAsset = (versionValues: any, curPreviewAssetId: any): FioPreviewContext | null => {
	const fioAssets = versionValues?.fio_asset_ids || {};

	if (curPreviewAssetId && fioAssets?.[curPreviewAssetId]) {
		return {
			provider: "fio",
			id: String(curPreviewAssetId),
			asset: fioAssets[curPreviewAssetId],
		};
	}

	return null;
};

const getPreviewSourceContext = (versionValues: any, curPreviewAssetId: any): PreviewContext => {
	return getFioAsset(versionValues, curPreviewAssetId) || getCoconutJob(versionValues, curPreviewAssetId) || getImageJob(versionValues, curPreviewAssetId) || null;
};

const getVideoHeightFromKey = (key: any) => {
	const text = String(key || "").toLowerCase();
	const match = text.match(/vid_h264_(\d{3,4})/) || text.match(/(\d{3,4})p/);

	if (!match?.[1]) {
		return -1;
	}

	const height = Number(match[1]);

	return Number.isFinite(height) ? height : -1;
};

const normalizeImageKeyLabel = (value: any) => {
	const key = String(value || "");

	if (key === "img_2160") {
		return "2160 Max Image";
	}

	if (key === "img_cover") {
		return "Cover Image";
	}

	if (key === "img_thumb") {
		return "Thumbnail Image";
	}

	return `${key.replace("img_", "").replace(/_/g, " ")} Image`;
};

const formatDownloadLabel = (download: DownloadItemData) => {
	const src = String(download.file_path || "");

	if (download.provider === "image") {
		const rendition = download.rendition || {};

		if (download.kind === "src") {
			return "Source File";
		}

		if (download.kind === "image") {
			if (rendition.role === "max_2160" || src === "img_2160") {
				return "2160 Max Image";
			}

			if (rendition.role === "cover" || src === "img_cover") {
				return "Cover Image";
			}

			if (rendition.role === "thumbnail" || src === "img_thumb") {
				return "Thumbnail Image";
			}

			return rendition.label || normalizeImageKeyLabel(src);
		}

		return rendition.label || src;
	}

	if (download.provider === "coconut") {
		const rendition = download.rendition || {};
		const height = rendition.height || getVideoHeightFromKey(src);

		if (download.kind === "src") {
			return "Source File";
		}

		if (download.kind === "video") {
			return height > 0 ? `${height}p` : rendition.label || src.replace("vid_h264_", "") || "Video";
		}

		if (download.kind === "image") {
			if (rendition.role === "cover" || src === "img_cover") {
				return "Cover Image";
			}

			if (rendition.role === "hover_image") {
				return "Hover Sprite";
			}

			if (rendition.role === "hover_vtt") {
				return "Hover VTT";
			}

			return rendition.label || `${src.replace("img_", "")} Image`;
		}

		return rendition.label || src;
	}

	const isVideo = src.includes("vid");
	const isImage = src.includes("img");

	return `${src.replace("vid_h264_", "").replace("_best", "").replace("src", "Source File").replace("img_", "")}${isVideo ? "p" : ""}${isImage ? " Image" : ""}`;
};

const getCoconutRenditionSize = (rendition: any, job: any, key: string) => {
	return (
		safeNumber(rendition?.size) ||
		safeNumber(rendition?.fileSize) ||
		safeNumber(rendition?.filesize) ||
		safeNumber(job?.downloadedOutputs?.[key]?.size) ||
		safeNumber(job?.downloadedOutputs?.[key]?.fileSize) ||
		safeNumber(job?.transcodes?.[key]?.size) ||
		safeNumber(job?.transcodes?.[key]?.fileSize) ||
		0
	);
};

const getCoconutRenditionPath = (rendition: any, job: any, key: string) => {
	return (
		rendition?.path ||
		rendition?.previewPath ||
		job?.preview?.[key] ||
		job?.downloadedOutputs?.[key]?.path ||
		job?.downloadedOutputs?.[key]?.previewPath ||
		job?.downloadedOutputs?.[key]?.destinationPath ||
		job?.transcodes?.[key]?.path ||
		job?.transcodes?.[key]?.previewPath ||
		job?.transcodes?.[key]?.destinationPath ||
		""
	);
};

const getImageRenditionSize = (rendition: any, job: any, key: string) => {
	return safeNumber(rendition?.size) || safeNumber(rendition?.fileSize) || safeNumber(rendition?.filesize) || safeNumber(job?.generatedOutputs?.[key]?.size) || safeNumber(job?.generatedOutputs?.[key]?.fileSize) || 0;
};

const getImageRenditionPath = (rendition: any, job: any, key: string) => {
	return rendition?.path || rendition?.previewPath || job?.preview?.[key] || job?.generatedOutputs?.[key]?.path || job?.generatedOutputs?.[key]?.previewPath || "";
};

const getCoconutAvailableDownloads = (uploadId: string, job: any): DownloadItemData[] => {
	const renditions = job?.renditions || {};
	const downloads: DownloadItemData[] = [];

	Object.entries(renditions).forEach(([key, rendition]: any) => {
		if (isSignedUrlKey(key)) {
			return;
		}

		const group = rendition?.group || "other";
		const role = rendition?.role || "";
		const mediaType = rendition?.mediaType || "";
		const path = getCoconutRenditionPath(rendition, job, key);

		if (!COCONUT_ALLOWED_GROUPS.includes(group)) {
			return;
		}

		if (!isStoragePath(path)) {
			return;
		}

		const isVideo = mediaType === "video" || group === "video" || String(key).startsWith("vid_");
		const isImage = mediaType === "image" || group === "cover" || group === "thumbnail" || role === "hover_image";
		const isText = mediaType === "text" || role === "hover_vtt";

		downloads.push({
			provider: "coconut",
			asset_id: uploadId,
			file_path: key,
			file_size: getCoconutRenditionSize(rendition, job, key),
			kind: isVideo ? "video" : isImage || isText ? "image" : "other",
			group,
			role,
			height: rendition?.height || getVideoHeightFromKey(key),
			rendition: {
				...rendition,
				path,
			},
		});
	});

	if (!downloads.length && job?.preview) {
		Object.entries(job.preview).forEach(([key, path]: any) => {
			if (isSignedUrlKey(key) || !isStoragePath(path)) {
				return;
			}

			const isVideo = String(key).startsWith("vid_");
			const isImage = String(key).startsWith("img_");

			downloads.push({
				provider: "coconut",
				asset_id: uploadId,
				file_path: key,
				file_size: safeNumber(job?.transcodes?.[key]?.size) || safeNumber(job?.downloadedOutputs?.[key]?.size) || 0,
				kind: isVideo ? "video" : isImage ? "image" : "other",
				group: isVideo ? "video" : isImage ? "cover" : "other",
				role: key === "img_cover" ? "cover" : "",
				height: getVideoHeightFromKey(key),
				rendition: {
					previewKey: key,
					path,
				},
			});
		});
	}

	const sourceSize = safeNumber(job?.fileSize) || safeNumber(job?.file_size) || safeNumber(job?.sourceFileSize) || 0;
	const sourcePath = job?.filePath || job?.sourcePath || "";

	if (sourcePath || sourceSize) {
		downloads.push({
			provider: "coconut",
			asset_id: uploadId,
			file_path: "src",
			file_size: sourceSize,
			kind: "src",
			group: "src",
			role: "source",
			height: -1,
			rendition: {
				path: sourcePath,
			},
		});
	}

	return downloads.sort((a, b) => {
		const order: Record<string, number> = {
			video: 0,
			image: 1,
			src: 2,
			other: 3,
		};

		if (order[a.kind] !== order[b.kind]) {
			return order[a.kind] - order[b.kind];
		}

		if (a.kind === "video") {
			return (b.height || -1) - (a.height || -1);
		}

		return String(a.file_path).localeCompare(String(b.file_path));
	});
};

const getImageAvailableDownloads = (uploadId: string, job: any): DownloadItemData[] => {
	const renditions = job?.renditions || {};
	const downloads: DownloadItemData[] = [];

	Object.entries(renditions).forEach(([key, rendition]: any) => {
		if (isSignedUrlKey(key)) {
			return;
		}

		const group = rendition?.group || "image";
		const role = rendition?.role || "";
		const mediaType = rendition?.mediaType || "image";
		const path = getImageRenditionPath(rendition, job, key);

		if (!IMAGE_ALLOWED_GROUPS.includes(group)) {
			return;
		}

		if (!isStoragePath(path)) {
			return;
		}

		const isImage = mediaType === "image" || group === "cover" || group === "thumbnail" || group === "source_proxy" || String(key).startsWith("img_");

		downloads.push({
			provider: "image",
			asset_id: uploadId,
			file_path: key,
			file_size: getImageRenditionSize(rendition, job, key),
			kind: isImage ? "image" : "other",
			group,
			role,
			height: -1,
			rendition: {
				...rendition,
				path,
			},
		});
	});

	if (!downloads.length && job?.preview) {
		Object.entries(job.preview).forEach(([key, path]: any) => {
			if (isSignedUrlKey(key) || !isStoragePath(path)) {
				return;
			}

			downloads.push({
				provider: "image",
				asset_id: uploadId,
				file_path: key,
				file_size: safeNumber(job?.generatedOutputs?.[key]?.size) || 0,
				kind: String(key).startsWith("img_") ? "image" : "other",
				group: key === "img_thumb" ? "thumbnail" : key === "img_cover" ? "cover" : key === "img_2160" ? "source_proxy" : "image",
				role: key === "img_thumb" ? "thumbnail" : key === "img_cover" ? "cover" : key === "img_2160" ? "max_2160" : "",
				height: -1,
				rendition: {
					previewKey: key,
					path,
				},
			});
		});
	}

	const sourceSize = safeNumber(job?.fileSize) || safeNumber(job?.file_size) || safeNumber(job?.sourceFileSize) || 0;
	const sourcePath = job?.filePath || job?.sourcePath || "";

	if (sourcePath || sourceSize) {
		downloads.push({
			provider: "image",
			asset_id: uploadId,
			file_path: "src",
			file_size: sourceSize,
			kind: "src",
			group: "src",
			role: "source",
			height: -1,
			rendition: {
				path: sourcePath,
			},
		});
	}

	const imageOrder: Record<string, number> = {
		img_2160: 0,
		img_cover: 1,
		img_thumb: 2,
	};

	return downloads.sort((a, b) => {
		const order: Record<string, number> = {
			image: 0,
			src: 1,
			other: 2,
			video: 3,
		};

		if (order[a.kind] !== order[b.kind]) {
			return order[a.kind] - order[b.kind];
		}

		if (a.kind === "image") {
			const aOrder = imageOrder[a.file_path] ?? 99;
			const bOrder = imageOrder[b.file_path] ?? 99;

			if (aOrder !== bOrder) {
				return aOrder - bOrder;
			}
		}

		return String(a.file_path).localeCompare(String(b.file_path));
	});
};

const getFioAvailableDownloads = (fioAssetId: string, fioAsset: any): DownloadItemData[] => {
	const availableDownloads: DownloadItemData[] = [];

	FIO_ALLOWED_DOWNLOADS.forEach((key: string) => {
		const currentValue = fioAsset?.asset_info?.file_sizes?.[key];

		if (typeof currentValue !== "undefined") {
			availableDownloads.push({
				provider: "fio",
				asset_id: fioAssetId,
				file_path: key,
				file_size: currentValue,
				kind: key.includes("vid") ? "video" : key.includes("img") ? "image" : "src",
				height: getVideoHeightFromKey(key),
			});
		}
	});

	return availableDownloads;
};

export default function DownloadMenu({
	curFioAssetID,
	onClick,
	version,
	canDownloadCurrentFrame = false,
	isDownloadingFrame = false,
	onDownloadCurrentFrame,
}: {
	curFioAssetID: any;
	version: any;
	onClick: any;
	canDownloadCurrentFrame?: boolean;
	isDownloadingFrame?: boolean;
	onDownloadCurrentFrame?: (format: FrameDownloadFormat) => void | Promise<void>;
}) {
	const authController = useAuthController();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [isDownloading, setIsDownloading] = useState<boolean>(false);

	const theme = useTheme();
	const open = Boolean(anchorEl);

	const defaultPrevent = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const handleClose = (event: any) => {
		defaultPrevent(event);
		setAnchorEl(null);
	};

	const handleClick = (event: any) => {
		defaultPrevent(event);

		if (typeof onClick === "function") {
			onClick();
		}

		setAnchorEl(event.currentTarget);
	};

	const versionValues = version?.values || {};
	const previewContext = getPreviewSourceContext(versionValues, curFioAssetID);

	let available_downloads: DownloadItemData[] = [];

	if (previewContext?.provider === "fio") {
		available_downloads = getFioAvailableDownloads(previewContext.id, previewContext.asset);
	} else if (previewContext?.provider === "coconut") {
		available_downloads = getCoconutAvailableDownloads(previewContext.id, previewContext.job);
	} else if (previewContext?.provider === "image") {
		available_downloads = getImageAvailableDownloads(previewContext.id, previewContext.job);
	}

	const formatBytes = (bytes: number, decimals = 2) => {
		if (!+bytes) return "";

		const k = 1024;
		const dm = decimals < 0 ? 0 : decimals;
		const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));

		return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
	};

	const downloadFile = (url: any) => {
		const a = document.createElement("a");
		a.href = url;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
	};

	const handleDownload = (download: DownloadItemData) => {
		const assetId = version?.values?.asset?.id || "";
		const versionId = version?.id || "";
		const previewAssetId = download?.asset_id || curFioAssetID || "";
		const src = download?.file_path || "";
		const provider = download?.provider || previewContext?.provider || "fio";

		if (!assetId || !versionId || !previewAssetId || !src) {
			return;
		}

		setIsDownloading(true);

		axios({
			url: `${authController?.extra?.apiUrl}/download/${assetId}/${versionId}/${previewAssetId}/${src}`,
			method: "GET",
			params: {
				provider,
			},
			headers: {
				Authorization: `boostToken ${authController?.extra?.user_data?.values?.boost_token}`,
			},
		})
			.then((res) => {
				setIsDownloading(false);

				if (res.data.success) {
					downloadFile(res.data.url);
					setAnchorEl(null);
				}
			})
			.catch((e) => {
				console.log(e);
				setIsDownloading(false);
			});
	};

	const DownloadRow = ({ download, disabled }: { download: DownloadItemData; disabled: boolean }) => {
		const src = download.file_path;
		const size = download.file_size;
		const label = formatDownloadLabel(download);
		const formattedSize = formatBytes(size);

		return (
			<MenuItem
				disabled={disabled}
				sx={{ "&:hover": { opacity: 1 }, opacity: 1 }}
				key={src}
				dense
				onClick={(evt) => {
					defaultPrevent(evt);
					handleDownload(download);
				}}
			>
				<ListItemText>
					<Box sx={{ display: "flex", flexDirection: "row", textTransform: "capitalize" }}>
						<Box sx={{ ml: 0.5 }}>
							<b>{label}</b>
						</Box>
						{formattedSize ? <Box sx={{ ml: 1, opacity: 0.65 }}>{formattedSize}</Box> : null}
					</Box>
				</ListItemText>
			</MenuItem>
		);
	};

	const available_vids = available_downloads.filter((d) => d.kind === "video");
	const available_imgs = available_downloads.filter((d) => d.kind === "image");
	const available_src = available_downloads.filter((d) => d.kind === "src");
	const available_other = available_downloads.filter((d) => d.kind === "other");

	const renderSectionItems = (title: string, items: DownloadItemData[]) => {
		if (!items.length) {
			return [];
		}

		return [
			<MenuItem key={`${title}_header`} sx={{ ml: 0.5 }} dense disabled>
				{title}
			</MenuItem>,
			...items.map((d) => {
				return <DownloadRow key={`${version?.id}_${d.provider}_${d.asset_id}_${d.file_path}_selector`} disabled={anyDownloadBusy} download={d} />;
			}),
		];
	};

	const providerLabel = previewContext?.provider === "coconut" ? "Coconut" : previewContext?.provider === "image" ? "Image" : previewContext?.provider === "fio" ? "Frame.io" : "";
	const anyDownloadBusy = isDownloading || isDownloadingFrame;

	const handleFrameDownload = (format: FrameDownloadFormat) => {
		if (typeof onDownloadCurrentFrame !== "function") {
			return;
		}

		const result = onDownloadCurrentFrame(format);

		if (result && typeof (result as Promise<void>).then === "function") {
			(result as Promise<void>)
				.then(() => {
					setAnchorEl(null);
				})
				.catch((error) => {
					console.log(error);
				});
		} else {
			setAnchorEl(null);
		}
	};

	const renderCurrentFrameItems = () => {
		if (!canDownloadCurrentFrame || typeof onDownloadCurrentFrame !== "function") {
			return [];
		}

		return [
			<MenuItem key="current_frame_header" sx={{ ml: 0.5 }} dense disabled>
				Current Frame
			</MenuItem>,
			<MenuItem
				key="current_frame_png"
				disabled={anyDownloadBusy}
				dense
				onClick={(evt) => {
					defaultPrevent(evt);
					handleFrameDownload("png");
				}}
			>
				<ListItemText>
					<Box sx={{ display: "flex", flexDirection: "row" }}>
						<Box sx={{ ml: 0.5 }}>
							<b>Current Frame PNG</b>
						</Box>
					</Box>
				</ListItemText>
			</MenuItem>,
			<MenuItem
				key="current_frame_jpg"
				disabled={anyDownloadBusy}
				dense
				onClick={(evt) => {
					defaultPrevent(evt);
					handleFrameDownload("jpeg");
				}}
			>
				<ListItemText>
					<Box sx={{ display: "flex", flexDirection: "row" }}>
						<Box sx={{ ml: 0.5 }}>
							<b>Current Frame JPG</b>
						</Box>
					</Box>
				</ListItemText>
			</MenuItem>,
		];
	};

	const menuItems =
		available_downloads.length || canDownloadCurrentFrame
			? [...renderCurrentFrameItems(), ...renderSectionItems("Videos", available_vids), ...renderSectionItems("Images", available_imgs), ...renderSectionItems("Source", available_src), ...renderSectionItems("Other", available_other)]
			: [
					<MenuItem key="no_downloads" dense disabled>
						<ListItemText>
							<Box sx={{ ml: 0.5, opacity: 0.65 }}>No downloads available</Box>
						</ListItemText>
					</MenuItem>,
			  ];

	return (
		<div>
			<IconButton onClick={handleClick} disableRipple size="small">
				<DownloadIcon fontSize="inherit" sx={{ cursor: "pointer", zIndex: 50000 }} />
			</IconButton>

			<Menu
				onClick={defaultPrevent}
				id="download-menu"
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				MenuListProps={{
					"aria-labelledby": "download-button",
				}}
				PaperProps={{
					style: {
						maxHeight: "33%",
						left: "50%",
						transform: "translateY(10px)",
					},
				}}
			>
				<ListSubheader key="downloads_header" component="div" sx={{ width: "100%", p: 0, m: 0 }}>
					<MenuItem sx={{ position: "sticky", background: `${theme.palette.primary.main}!important`, opacity: "1!important", pl: 2.5, pt: 1, pb: 1, m: 0, mt: -1, mb: 0.5 }} dense disabled>
						{anyDownloadBusy ? (
							<Box sx={{ width: "100%" }}>
								<LinearProgress />
							</Box>
						) : (
							`Downloads${providerLabel ? ` · ${providerLabel}` : ""}`
						)}
					</MenuItem>
				</ListSubheader>

				{menuItems}
			</Menu>
		</div>
	);
}
